/**
 * GroupPicker — Feb 2026 contact-grouping arc.
 *
 * A typeahead combobox over `/api/contact-groups` with a "create new" path.
 * Used in:
 *   - the Contacts edit form (full inline picker)
 *   - the My Desk capture-completion queued dialog
 *
 * Behavior:
 *   - Value = group id (string | null). null = "Unassigned".
 *   - Suggests existing groups by case-insensitive substring on `name_lower`.
 *   - "Create new" path requires user to choose a type (company | family).
 *     The default is suggested by the parent (defaultType prop) — typically
 *     "company" if the contact has a company field filled in, "family"
 *     otherwise.
 *   - Renders the linked group's CURRENT name + type pill when a value is set.
 *
 * data-testids:
 *   group-picker-input
 *   group-picker-suggestion-{group_id}
 *   group-picker-create-{type}
 *   group-picker-unassign
 *   group-picker-current
 */

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Building2, Users, Globe, X, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export default function GroupPicker({
  value,                 // current group_id (string | null)
  onChange,              // (newGroupId: string | null) => void
  defaultType = 'company', // 'company' | 'family' — used when "Create new"
  defaultName = '',       // pre-filled name when nothing is linked yet
  className = '',
  disabled = false,
}) {
  const [allGroups, setAllGroups] = useState([]);
  const [query, setQuery] = useState(defaultName || '');
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastFetchRef = useRef(0);

  // Initial + refresh load. Cached for ~30s so the picker doesn't refetch on
  // every focus/blur in a busy form. Bypassed when query changes.
  const fetchGroups = async () => {
    const now = Date.now();
    if (now - lastFetchRef.current < 30_000) return;
    lastFetchRef.current = now;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contact-groups`);
      setAllGroups(res.data?.groups || []);
    } catch {
      setAllGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); }, []);

  // When `defaultName` (typically the company or last_name field) changes
  // from the parent form, refresh the query to that name UNLESS the user
  // is currently typing.
  useEffect(() => {
    if (!focused) setQuery(defaultName || '');
  }, [defaultName, focused]);

  const selected = value ? allGroups.find(g => g.id === value) : null;

  const normalized = (query || '').trim().toLowerCase();
  const suggestions = normalized
    ? allGroups.filter(g => g.name_lower.includes(normalized)).slice(0, 12)
    : allGroups.slice(0, 12);
  const exactExisting = normalized
    ? allGroups.find(g => g.name_lower === normalized)
    : null;

  const handlePickExisting = (group) => {
    setQuery(group.name);
    setFocused(false);
    onChange?.(group.id);
  };

  const handleCreate = async (type) => {
    const name = (query || '').trim();
    if (!name) return;
    try {
      const res = await axios.post(`${API}/contact-groups`, { name, type });
      // Reset cache so the next focus picks up the new group.
      lastFetchRef.current = 0;
      await fetchGroups();
      setFocused(false);
      setQuery(res.data.name);
      onChange?.(res.data.id);
    } catch (err) {
      console.error('Failed to create group', err);
    }
  };

  const handleUnassign = () => {
    setQuery('');
    setFocused(false);
    onChange?.(null);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Currently-selected group preview */}
      {selected && !focused && (
        <div
          className="flex items-center gap-2 p-2 border rounded-md bg-muted/30"
          data-testid="group-picker-current"
        >
          {selected.type === 'company'
            ? <Building2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
            : selected.type === 'organization'
              ? <Globe className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              : <Users className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
          <span className="text-sm font-medium truncate">{selected.name}</span>
          <Badge variant="outline" className="text-[10px] h-4 px-1 capitalize">{selected.type}</Badge>
          <span className="text-xs text-muted-foreground ml-auto">{selected.member_count} member{selected.member_count === 1 ? '' : 's'}</span>
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 shrink-0"
              onClick={handleUnassign}
              title="Unassign (mark as Unassigned)"
              data-testid="group-picker-unassign"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-[10px] px-1.5"
              onClick={() => { setQuery(''); setFocused(true); }}
            >
              Change
            </Button>
          )}
        </div>
      )}

      {/* Combobox input */}
      {(!selected || focused) && (
        <>
          <Input
            value={query}
            onChange={(e) => { setQuery(e.target.value); if (!focused) setFocused(true); }}
            onFocus={() => setFocused(true)}
            placeholder="Type to search or create a group…"
            disabled={disabled}
            data-testid="group-picker-input"
          />
          {focused && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 max-h-72 overflow-y-auto border bg-popover rounded-md shadow-md text-sm">
              {loading && (
                <div className="px-3 py-2 text-xs text-muted-foreground">Loading groups…</div>
              )}
              {!loading && suggestions.length === 0 && !normalized && (
                <div className="px-3 py-2 text-xs text-muted-foreground">No groups yet. Type a name to create one.</div>
              )}
              {suggestions.map(g => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => handlePickExisting(g)}
                  data-testid={`group-picker-suggestion-${g.id}`}
                  className="w-full text-left flex items-center gap-2 px-3 py-1.5 hover:bg-muted"
                >
                  {g.type === 'company'
                    ? <Building2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                    : g.type === 'organization'
                      ? <Globe className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                      : <Users className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
                  <span className="truncate">{g.name}</span>
                  <Badge variant="outline" className="text-[10px] h-4 px-1 capitalize ml-auto">{g.type}</Badge>
                  <span className="text-[10px] text-muted-foreground">{g.member_count}</span>
                </button>
              ))}
              {/* Create-new section (only when query is non-empty and no
                  exact existing match) */}
              {normalized && !exactExisting && (
                <div className="border-t p-2 bg-muted/40">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Create new</div>
                  <div className="flex gap-1 flex-wrap">
                    <Button
                      type="button"
                      size="sm"
                      variant={defaultType === 'company' ? 'default' : 'outline'}
                      onClick={() => handleCreate('company')}
                      data-testid="group-picker-create-company"
                      className="flex-1 min-w-[110px]"
                    >
                      <Building2 className="w-3.5 h-3.5 mr-1" />Company &ldquo;{query}&rdquo;
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={defaultType === 'family' ? 'default' : 'outline'}
                      onClick={() => handleCreate('family')}
                      data-testid="group-picker-create-family"
                      className="flex-1 min-w-[110px]"
                    >
                      <Users className="w-3.5 h-3.5 mr-1" />Family &ldquo;{query}&rdquo;
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={defaultType === 'organization' ? 'default' : 'outline'}
                      onClick={() => handleCreate('organization')}
                      data-testid="group-picker-create-organization"
                      className="flex-1 min-w-[110px]"
                    >
                      <Globe className="w-3.5 h-3.5 mr-1" />Org &ldquo;{query}&rdquo;
                    </Button>
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={handleUnassign}
                className="w-full text-left px-3 py-1.5 hover:bg-muted text-xs text-muted-foreground border-t"
                data-testid="group-picker-unassign"
              >
                <X className="w-3 h-3 inline mr-1" />Leave Unassigned (complete later)
              </button>
            </div>
          )}
          {focused && (
            <div className="fixed inset-0 z-10" onClick={() => setFocused(false)} />
          )}
        </>
      )}
    </div>
  );
}
